/// <reference path="./globals.d.ts" />
import type { OktaAuthOptions } from '@okta/okta-auth-js';

export type OktaConfig = OktaAuthOptions;

export interface OktaWindowVars {
  /** Okta client id injected by the .NET backend or set in dev */
  oktaClientId?: string;
  /** Full Okta issuer URL */
  oktaIssuer?: string;
  /** Space-separated Okta scopes */
  oktaScopes?: string;
  /** Application base path (e.g. "/Load/", "/Security/") */
  baseUrl?: string;
}

export interface OktaEnvVars {
  VITE_OKTA_CLIENT_ID?: string;
  VITE_OKTA_ISSUER?: string;
  VITE_OKTA_DOMAIN?: string;
  VITE_OKTA_AUTH_SERVER_ID?: string;
  VITE_OKTA_SCOPES?: string;
}

export interface BuildOktaConfigOptions {
  /**
   * Per-app default base URL when neither `window.baseUrl` nor any other
   * source is available. e.g. `/Security/` for Security-React, `/` for Load.
   */
  defaultBaseUrl?: string;
  /**
   * Optional override for environment variables. Apps normally rely on the
   * default (`import.meta.env`); tests pass an object to drive specific
   * code paths.
   */
  env?: OktaEnvVars;
  /**
   * Optional override for window-injected variables. Apps normally rely on
   * the default (`window`); tests pass an object to drive specific paths.
   */
  win?: OktaWindowVars;
}

const DEFAULT_SCOPES = 'openid profile email';

/**
 * Build the Okta config from runtime/window/env. Returns null when no Okta
 * config is available (e.g., a CAD-only deployment), in which case the
 * unified AuthProvider falls back to CAD or renders children without auth.
 *
 * Apps should call this and pass the result to `<AuthProvider oktaConfig={...} />`
 * — they never need to import from `@okta/*` directly.
 */
export function buildOktaConfigFromWindow(
  options: BuildOktaConfigOptions = {},
): OktaConfig | null {
  // Source values: prefer explicit overrides, then runtime globals.
  // Window/env fields are declared globally in `globals.d.ts`, so direct
  // access is fully typed without needing local casts.
  const win: OktaWindowVars = options.win ?? window;
  const env: OktaEnvVars = options.env ?? import.meta.env;

  const clientId = win.oktaClientId || env.VITE_OKTA_CLIENT_ID;
  const issuer =
    win.oktaIssuer ||
    (env.VITE_OKTA_DOMAIN
      ? `${env.VITE_OKTA_DOMAIN}/oauth2/${env.VITE_OKTA_AUTH_SERVER_ID || 'default'}`
      : env.VITE_OKTA_ISSUER);
  const scopes =
    win.oktaScopes?.trim() || env.VITE_OKTA_SCOPES || DEFAULT_SCOPES;
  const baseUrl = win.baseUrl || options.defaultBaseUrl || '/';
  const redirectUri = `${window.location.origin}${baseUrl}login/callback`;

  if (!clientId || !issuer) {
    return null;
  }

  return {
    clientId,
    issuer,
    redirectUri,
    scopes: scopes.split(' ').filter(Boolean),
    pkce: true,
    tokenManager: { autoRenew: true },
    postLogoutRedirectUri: window.location.origin + baseUrl,
  };
}
